import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ExceptionalOpeningHoursV2,
  ExceptionalOpeningTimeSentinel,
  OpeningTimeRange,
} from '../../../model/opening-hours';
import { OpeningTimeValue } from '../opening-time-editor/opening-time-editor.component';
import moment, { Moment } from 'moment';

@Component({
  selector: 'gymfinder-exceptional-opening-hour',
  templateUrl: './exceptional-opening-hours-day.component.html',
  styleUrls: ['./exceptional-opening-hours-day.component.scss'],
  standalone: false,
})
export class ExceptionalOpeningHourComponent {
  @Input()
  editMode: boolean;

  @Input()
  set exception(val: ExceptionalOpeningHoursV2) {
    this.periodStart = val.period?.startDate ?? '';
    this.periodEnd = val.period?.endDate ?? '';
    this.editorValue = this.toEditorValue(val.time);
  }

  @Output()
  exceptionChange = new EventEmitter<ExceptionalOpeningHoursV2>();

  @Output()
  removeException = new EventEmitter<void>();

  periodStart = '';
  periodEnd = '';
  editorValue: OpeningTimeValue = { kind: 'closed' };

  get periodStartMoment(): Moment | null {
    return this.periodStart ? moment(this.periodStart) : null;
  }

  get periodEndMoment(): Moment | null {
    return this.periodEnd ? moment(this.periodEnd) : null;
  }

  get periodError(): string | null {
    if (!this.periodStart || !this.periodEnd) return null;
    if (this.periodStart > this.periodEnd) {
      return 'Start date must be before or equal to end date';
    }
    return null;
  }

  onPeriodStartChange(value: Moment | null) {
    this.periodStart = value ? value.format('YYYY-MM-DD') : '';
    this.emit();
  }

  onPeriodEndChange(value: Moment | null) {
    this.periodEnd = value ? value.format('YYYY-MM-DD') : '';
    this.emit();
  }

  onEditorValueChange(val: OpeningTimeValue) {
    this.editorValue = val;
    this.emit();
  }

  remove() {
    this.removeException.emit();
  }

  private emit() {
    this.exceptionChange.emit({
      period: { startDate: this.periodStart, endDate: this.periodEnd },
      time: this.toExceptionTime(this.editorValue),
    });
  }

  private toExceptionTime(
    value: OpeningTimeValue
  ): OpeningTimeRange[] | ExceptionalOpeningTimeSentinel {
    switch (value.kind) {
      case 'open24':
        return 'OPEN_24_HOURS';
      case 'ranges':
        return value.ranges;
      case 'closed':
        return 'CLOSED_24_HOURS';
      case 'not-available':
        throw new Error(
          'Illegal state - exceptional opening time must not be not-available'
        );
    }
  }

  private toEditorValue(
    val: OpeningTimeRange[] | ExceptionalOpeningTimeSentinel | undefined
  ): OpeningTimeValue {
    if (Array.isArray(val)) {
      return { kind: 'ranges', ranges: val };
    }
    switch (val) {
      case 'OPEN_24_HOURS':
        return { kind: 'open24' };
      case 'CLOSED_24_HOURS':
      default:
        return { kind: 'closed' };
    }
  }
}
